import * as React from "react";
import { useState } from "react";
import { redirect } from "react-router";
import { CircleAlert, Eye, EyeOff, Loader2, LockKeyhole } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import Container from "@/components/Container";
import Logo from "@/components/common/logo";
import Medallion from "@/components/common/Medallion";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export async function loginLoader() {
  const { data } = await authClient.getSession();
  if (data?.user) throw redirect("/admin");
  return null;
}

/** Text input: 16px below sm so iOS never zooms on focus, soft emerald focus ring. */
export const INPUT_CLASS =
  "block h-11 w-full rounded-control border-paper-300 bg-white/70 px-3.5 text-base text-ink placeholder:text-ink-subtle/70 transition-[border-color,box-shadow] duration-150 ease-out focus:border-emerald-700 focus:ring-4 focus:ring-emerald-700/15 sm:text-sm";

/** Sticky translucent bar with the logo; shared look for Login and Admin. */
export function AuthHeader({ children }: React.PropsWithChildren) {
  return (
    <header className="sticky top-0 z-40 border-b border-paper-300/70 bg-paper-50/80 pt-[env(safe-area-inset-top)] backdrop-blur-md">
      <Container className="flex h-16 items-center justify-between gap-3">
        <Logo />
        {children}
      </Container>
    </header>
  );
}

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorId = React.useId();

  const signIn = async (e: React.FormEvent) => {
    e.preventDefault();

    await authClient.signIn.email(
      {
        email,
        password,
      },
      {
        onRequest: () => {
          setIsSubmitting(true);
          setError("");
        },
        onSuccess: () => {
          window.location.href = "/admin";
        },
        onError: (ctx) => {
          setError(
            (ctx.error.status === 401 && "Pogrešan e-mail ili lozinka.") ||
              ctx.error.message ||
              "Nešto je pošlo po zlu. Molimo pokušajte ponovo."
          );
          setIsSubmitting(false);
        },
      }
    );
  };

  const describedBy = error ? errorId : undefined;

  return (
    <div className="flex min-h-svh flex-col bg-paper-50">
      <AuthHeader />

      <main className="flex flex-1 justify-center px-4 pb-16 pt-[12vh]">
        <div className="h-fit w-full max-w-sm rounded-2xl bg-paper-50 p-6 shadow-card ring-1 ring-paper-300 sm:p-8">
          <Medallion className="mx-auto size-12">
            <LockKeyhole className="size-4" />
          </Medallion>
          <h1 className="mt-5 text-center text-2xl font-semibold tracking-tight text-ink">
            Prijavite se na vaš račun
          </h1>

          <form className="mt-8 space-y-5" onSubmit={signIn}>
            <div>
              <label htmlFor="email" className="text-sm font-medium text-ink">
                E-mail
              </label>
              <input
                id="email"
                name="email"
                autoComplete="email"
                type="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                aria-invalid={error ? true : undefined}
                aria-describedby={describedBy}
                className={cn(INPUT_CLASS, "mt-1.5")}
                placeholder="Unesite vaš e-mail"
              />
            </div>

            <div>
              <label htmlFor="password" className="text-sm font-medium text-ink">
                Lozinka
              </label>
              <div className="relative mt-1.5">
                <input
                  id="password"
                  name="password"
                  autoComplete="current-password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  aria-invalid={error ? true : undefined}
                  aria-describedby={describedBy}
                  className={cn(INPUT_CLASS, "pr-11")}
                  placeholder="Unesite vašu lozinku"
                />
                {/* The icon names the action: EyeOff hides, Eye shows. */}
                <button
                  type="button"
                  aria-label={showPassword ? "Sakrij lozinku" : "Prikaži lozinku"}
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 grid w-11 place-items-center rounded-r-control text-ink-subtle transition-colors duration-150 hover:text-ink"
                >
                  {showPassword ? (
                    <EyeOff aria-hidden className="size-4" />
                  ) : (
                    <Eye aria-hidden className="size-4" />
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div
                id={errorId}
                role="alert"
                className="flex items-start gap-2 rounded-control bg-rose-50 px-3 py-2.5 text-sm text-rose-800 ring-1 ring-rose-200"
              >
                <CircleAlert aria-hidden className="mt-0.5 size-4 shrink-0" />
                {error}
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full disabled:opacity-80"
            >
              {isSubmitting ? (
                <>
                  <Loader2 aria-hidden className="size-4 animate-spin" />
                  Prijava u toku...
                </>
              ) : (
                "Prijavite se"
              )}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
}
